import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useJwtStorage } from "../Storage/useJwtStorage";
import { useProfileStorage } from "../Storage/useProfileStorage";
import { updateProfile } from "@/libs/ApiFetchMeme";
import { useProfileTabsStorage } from "../Storage/useProfileTabsStorage";
import { TeamSelect } from "@/types/team";
import { create } from "zustand";
import { createTrackedSelector } from "react-tracked";

interface EditProfileContextInterface {
  team: TeamSelect | undefined;
  setTeam: (team: TeamSelect | undefined) => void;
  isTeam: boolean;
  setIsTeam: (isTeam: boolean) => void;
}

export const EditProfileContext = create<EditProfileContextInterface>(
  (set) => ({
    team: undefined,
    setTeam: (team: TeamSelect | undefined) => set({ team }),
    isTeam: false,
    setIsTeam: (isTeam: boolean) => set({ isTeam }),
  })
);

const useEditProfileContext = createTrackedSelector(EditProfileContext);

export const useEditProfile = (shouldValidateProfile?: boolean) => {
  const { team, setTeam, isTeam, setIsTeam } = useEditProfileContext();

  const { jwt, setJwt, removeJwt } = useJwtStorage();
  const { profile, setProfile, removeProfile } = useProfileStorage();
  const [showOpenEditProfileModal, setShowOpenEditProfileModal] =
    useState(false);
  const [file, setFile] = useState<Blob | null>(null);
  const [fileName, setFileName] = useState(profile?.profile_picture_url ?? "");
  const [name, setName] = useState(profile?.name || "");
  const [bio, setBio] = useState(profile?.bio ?? "");
  const [isFirstFetch, setIsFirstFetch] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { tabs, setTabs } = useProfileTabsStorage();
  const [guestTabs, setGuestTabs] = useState(0);

  useEffect(() => {
    setFileName(profile?.profile_picture_url ?? "");
    setName(profile?.name || "");
    setBio(profile?.bio ?? "");
    setTeam((profile?.team as TeamSelect) ?? undefined);
    setIsTeam(!!profile?.team);
  }, [
    profile?.bio,
    profile?.name,
    profile?.profile_picture_url,
    profile?.team,
    setTeam,
    setIsTeam,
  ]);

  const handleSave = useCallback(async () => {
    setIsSubmitting(true);
    setIsLoading(true);

    if (jwt) {
      let usingName = name;
      if (name === "") {
        usingName = `@${profile?.user_address.slice(0, 6)}`;
      }
      const newProfile = await updateProfile(
        usingName,
        bio,
        team,
        file ?? undefined,
        jwt
      );

      if (newProfile.data) {
        setProfile({
          bio: newProfile.data.bio,
          created: newProfile.data.created,
          follower_count: newProfile.data.following_count,
          liked_recieve: newProfile.data.liked_recieve,
          mentioned_recieve: newProfile.data.mentioned_recieve,
          name: newProfile.data.name,
          profile_picture_id: newProfile.data.profile_picture_id,
          profile_picture_url: newProfile.data.profile_picture_url,
          team: newProfile.data.team,
          updated: newProfile.data.updated,
          user_address: newProfile.data.user_address,
        });
        setIsTeam(!!newProfile.data.team);
      }
    }

    setIsSubmitting(false);
    setIsLoading(false);
    setIsFirstFetch(true);

    setShowOpenEditProfileModal(false);
  }, [
    bio,
    file,
    jwt,
    name,
    profile?.user_address,
    setIsTeam,
    setProfile,
    team,
  ]);

  const handleDisconnetClick = useCallback(async () => {
    // disconnect();
    removeJwt();
    removeProfile();
  }, [removeJwt, removeProfile]);

  const errorMessage: string | undefined = useMemo(() => {
    if (file && file?.size > 1_048_576) {
      return "Maximum file size is 1MB";
    }

    if (name === "") {
      return "Please enter a name";
    }

    // if (!team) {
    //   return "Please select a team";
    // }

    if (bio && bio.length > 255) {
      return "Maximum text is 255 characters";
    }
  }, [bio, file, name]);

  const buttonText: string | undefined = useMemo(() => {
    if (isSubmitting) {
      return `Saving...`;
    }

    return `Save`;
  }, [isSubmitting]);

  const hiddenFileInput = useRef<any>(null);

  const handleFileClick = useCallback(() => {
    hiddenFileInput.current?.click();
  }, []);

  const handleFile = useCallback((file: any) => {
    setFileName(URL.createObjectURL(file));
    setFile(file);
  }, []);

  const handleFileChange = useCallback(
    (event: any) => {
      const fileUploaded = event.target.files[0];
      handleFile?.(fileUploaded);
    },
    [handleFile]
  );

  const handleNameChange = useCallback((e: any) => {
    setName(e.target.value);
  }, []);

  const handleBioChange = useCallback((e: any) => {
    setBio(e.target.value);
  }, []);

  const handleTeamChange = useCallback(
    (e: TeamSelect) => {
      setTeam(e);
    },
    [setTeam]
  );

  return {
    showOpenEditProfileModal,
    setShowOpenEditProfileModal,
    file,
    setFile,
    fileName,
    setFileName,
    name,
    setName,
    bio,
    setBio,
    team,
    setTeam,
    isSubmitting,
    setIsSubmitting,
    hiddenFileInput,
    handleSave,
    handleDisconnetClick,
    errorMessage,
    buttonText,
    handleFileClick,
    handleFile,
    handleFileChange,
    handleNameChange,
    handleBioChange,
    handleTeamChange,
    profile,
    isFirstFetch,
    isLoading,
    jwt,
    tabs,
    setTabs,
    guestTabs,
    setGuestTabs,
    isTeam,
    setIsTeam,
  };
};
